import React from 'react'
import { Redirect, Route, Switch, useHistory, generatePath } from 'react-router'
import loadable from '@loadable/component'
import { useSelector, useDispatch } from 'react-redux'
import { newPaths, paths } from 'routes'
import lazyRetry from 'utils/lazyRetry'
import { NotificationList } from 'pages/Notification/NotificationList'
import { IAppState } from 'infra/AppState'
import { ENTITY_TYPE } from 'shared/constants'
import DefaulterPage from 'pages/DefaulterPage'
import { useAbExperiment } from 'utils/useAbExperiment'
import ManagerDetails from 'pages/Dashboard/components/Drawers/ManagerDetails'
import { SelectAccountModal } from 'pages/Auth/components/SelectAccountModal'
import { closeSelectAccountModal } from 'state/auth/auth.actions'
import { TransactionAlertNudge } from 'components/TransactionAlertSettings/TransactionAlertDialog'
import { SecurityPinHost } from 'components/SecurityPin/SecurityPinHost'
import Header from './components/Header'
import ExpiryBanner from './components/ExpiryBanner'
import SyncScreen from './components/SyncScreen'

const Summary = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Summary" */ './components/Summary'))
)

const ApproveRoutes = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "ApproveRoutes" */ './components/ApproveRoutes'))
)

const SupplyList = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "SupplyList" */ './components/SupplyList'))
)

const CollectionList = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "CollectionList" */ './components/CollectionList'))
)

const Ledger = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "CollectionList" */ '../../pages/Ledger'))
)

const CustomerProfiles = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "CollectionList" */ './components/CustomerProfiles'))
)

const Collection = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Collection" */ '../../pages/Collection'))
)

const Reconcile = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Reconcile" */ './components/Reconcile'))
)

const Billing = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Billing" */ './components/Billing'))
)

const Brand = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Billing" */ './components/Brand'))
)

const MyPlan = loadable(() => lazyRetry(() => import('./components/MyPlan')))
const Help = loadable(() => lazyRetry(() => import('./components/HelpSection')))
const Settings = loadable(() => lazyRetry(() => import('../Dashboard/components/Drawers/Settings')))
const StaffSettings = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/StaffSettings'))
)
const StaffSettingsDistanceTravelled = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/StaffSettingsDistanceTravelled'))
)
const StaffCheckinPhoto = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/StaffCheckinPhoto'))
)
const SwitchAccount = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/SwitchAccount'))
)

const MerchantProfileEditor = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/MerchantProfileEditor'))
)

const TagManagement = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/Tags/TagManagement'))
)

const Reminder = loadable(() => lazyRetry(() => import('../Dashboard/components/Drawers/Reminder')))

const AccountTags = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/Tags/AccountTags'))
)

const AccountBeats = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/Beats/AccountBeats'))
)

const AccountRoutes = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/Beats/AccountRoutes'))
)

const TransactionDetail = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/TransactionDetail'))
)

const AddCustomerSupplierPopup = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/AddCustomerSupplierPopup'))
)
const AddCustomerSupplierSuccessPopup = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/AddCustomerSupplierSuccessPopup'))
)
const AddTransaction = loadable(() =>
  lazyRetry(() => import('pages/Transactions/components/AddTransaction'))
)
const CustomerSupplierProfileEditor = loadable(() =>
  lazyRetry(() => import('../Dashboard/components/Drawers/CustomerSupplierProfileEditor'))
)

const CommonRootLevelDrawers = loadable(() =>
  lazyRetry(() => import(/* webpackChunkName: "Root Drawers" */ './components/CommonDrawers'))
)

const NewDashboard = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { staffLink } = useAbExperiment()
  const navigationState = useSelector((appState: IAppState) => appState.Navigation)
  const entity = navigationState.currentRoute.navParams.entity || ENTITY_TYPE.CUSTOMER
  const id = navigationState.currentRoute.navParams.id || ''

  const authState = useSelector((appState: IAppState) => appState.Auth)
  const isSelectAccountDialogOpen = authState?.isSelectAccountDialogOpen
  const { currentBusinessConfig } = useSelector((app: IAppState) => app.Dashboard)
  const visibleScreens = currentBusinessConfig?.visible_screens
    ? currentBusinessConfig?.visible_screens
    : undefined
  const seed = useSelector((app: IAppState) => app.Seed)
  const seedActiveBusinessId = seed.activeBusinessId
  const activeSeedState = seedActiveBusinessId ? seed.byBusinessId[seedActiveBusinessId] : undefined
  const getDefaultRedirectUrl = () => {
    const url = staffLink ? newPaths.approve : newPaths.ledger
    if (!visibleScreens) {
      return url
    }
    return newPaths.approve
  }

  const onClose = () => {
    if (navigationState?.prevRoute?.pathName) {
      history.goBack()
    } else {
      history.push(generatePath(getDefaultRedirectUrl()))
    }
  }

  // Block the dashboard with the sync screen only for the ACTIVE business while it
  // is actively SEEDING. Any terminal state (done / failed / skipped) renders the
  // dashboard normally, so this is never a dead end. In particular 'failed' must NOT
  // block: a deterministic seed failure otherwise trapped users behind an
  // infinite-retry "Sync Failed" screen. The ledger read path falls back to REST
  // (lazy fetch) when the local cache is empty, so a failed seed degrades gracefully.
  if (seedActiveBusinessId && activeSeedState?.status === 'seeding') {
    return <SyncScreen businessId={seedActiveBusinessId} />
  }

  return (
    <React.Fragment>
      <Route path={newPaths.base}>
        <div
          style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
          <Header>
            <Switch>
              <Route path={newPaths.approve} render={() => <ApproveRoutes />} />
              <Route path={newPaths.supplyList} render={() => <SupplyList />} />
              <Route path={newPaths.collectionList} render={() => <CollectionList />} />
              <Route path={newPaths.summary} render={() => <Summary />} />

              {/* Also matches the add-transaction URL. That dialog is rendered outside this
                  Switch (see below) so it overlays the ledger instead of replacing it — a
                  Switch renders only its first match, so without this the ledger would be
                  unmounted the moment the dialog opens and rebuilt from scratch on close,
                  which is exactly the flicker the customer list used to show. */}
              <Route
                path={[newPaths.ledger, newPaths.addTransaction]}
                exact
                render={() => <Ledger />}
              />
              <Route path={newPaths.myPlan} exact render={() => <MyPlan />} />
              {/* 1-Click Vasooli — always on (Android parity, the AB flag was retired);
                  staff-link sessions are the only exclusion, same as the Collection tab */}
              <Route
                path={newPaths.collection}
                exact
                render={() => (!staffLink ? <Collection /> : <Redirect to={newPaths.ledger} />)}
              />
              <Route path={newPaths.customersProfile} render={() => <CustomerProfiles />} />
              <Route path={newPaths.reconcile} render={() => <Reconcile />} />
              <Route path={newPaths.billing} render={() => <Billing />} />
              <Route path={newPaths.brand} render={() => <Brand />} />
              <Route path={newPaths.help} render={() => <Help onClose={onClose} />} />
              <Route exact path={newPaths.settings} render={() => <Settings onClose={onClose} />} />
              <Route exact path={newPaths.stafSettings} render={() => <StaffSettings />} />
              <Route
                exact
                path={newPaths.stafDistanceTravelled}
                render={() => <StaffSettingsDistanceTravelled />}
              />
              <Route exact path={newPaths.staffCheckInImage} render={() => <StaffCheckinPhoto />} />
              <Route exact path={newPaths.editMerchant} render={() => <MerchantProfileEditor />} />
              <Route
                path={newPaths.manageAccounts}
                render={() => <SwitchAccount onClose={onClose} />}
                exact
              />
              <Route
                path={newPaths.managerDetails}
                render={() => <ManagerDetails onClose={onClose} />}
                exact
              />
              <Route
                exact
                path={newPaths.tags}
                render={() => <TagManagement onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.reminders}
                render={() => <Reminder onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.editEntity}
                render={() => (
                  <CustomerSupplierProfileEditor
                    onClose={onClose}
                    entityType={entity}
                    entityId={id}
                  />
                )}
              />
              <Route
                exact
                path={newPaths.addEntity}
                render={() => <AddCustomerSupplierPopup entityType={entity} onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.addEntitySuccess}
                render={() => (
                  <AddCustomerSupplierSuccessPopup entityType={entity} onClose={onClose} />
                )}
              />
              <Route
                exact
                path={newPaths.transactionDetails}
                render={() => <TransactionDetail onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.accountTags}
                render={() => <AccountTags onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.accountBeats}
                render={() => <AccountBeats onClose={onClose} />}
              />
              <Route
                exact
                path={newPaths.accountRoutes}
                render={() => <AccountRoutes onClose={onClose} />}
              />
              <Redirect to={getDefaultRedirectUrl()} />
            </Switch>
          </Header>
          <ExpiryBanner />
        </div>
        <NotificationList />
        <CommonRootLevelDrawers />
        {/* Deliberately outside the Switch: AddTransaction renders a fixed-position Popup,
            so as a parallel route it floats over the still-mounted ledger. Moving it back
            into the Switch would reintroduce the unmount/remount flicker. */}
        <Route
          exact
          path={newPaths.addTransaction}
          render={() => <AddTransaction onClose={onClose} />}
        />
      </Route>
      <Route path={paths.defaulterLink}>
        <DefaulterPage />
      </Route>
      {isSelectAccountDialogOpen && (
        <SelectAccountModal onClose={() => dispatch(closeSelectAccountModal())} />
      )}
      {/* Post-transaction "set up Transaction Alert" nudge (iOS/Android parity) */}
      <TransactionAlertNudge />
      {/* Security PIN dialog, opened through requireSecurityPin() */}
      <SecurityPinHost />
    </React.Fragment>
  )
}

export default NewDashboard
